import {
  doc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  QueryConstraint,
  DocumentReference,
  Timestamp,
} from "firebase/firestore";
import { db } from "./firebase";

// Convert Firestore doc to typed object with uid
export function docToTyped<T extends { uid?: string }>(
  snapshot: { id: string; data: () => Record<string, unknown> | undefined }
): T | null {
  const data = snapshot.data();
  if (!data) return null;
  return { ...data, uid: snapshot.id } as T;
}

// Fetch a single document by path
export async function fetchDocument<T extends { uid?: string }>(
  path: string,
  documentId: string
): Promise<T | null> {
  const ref = doc(db, path, documentId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;
  return { ...snapshot.data(), uid: snapshot.id } as T;
}

// Fetch a collection with optional query constraints
export async function fetchCollection<T extends { uid?: string }>(
  path: string,
  ...constraints: QueryConstraint[]
): Promise<T[]> {
  const ref = collection(db, path);
  const q = constraints.length > 0 ? query(ref, ...constraints) : query(ref);
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ ...d.data(), uid: d.id }) as T);
}

// Set (upsert) a document
export async function setDocument<T extends Record<string, unknown>>(
  path: string,
  documentId: string,
  data: T,
  merge = true
): Promise<void> {
  const ref = doc(db, path, documentId);
  await setDoc(ref, data, { merge });
}

// Update specific fields
export async function updateDocument(
  path: string,
  documentId: string,
  data: Record<string, unknown>
): Promise<void> {
  const ref = doc(db, path, documentId);
  await updateDoc(ref, data);
}

// Delete a document
export async function removeDocument(
  path: string,
  documentId: string
): Promise<void> {
  const ref = doc(db, path, documentId);
  await deleteDoc(ref);
}

// Get a document reference (useful for batch operations)
export function getDocRef(path: string, documentId: string): DocumentReference {
  return doc(db, path, documentId);
}

// Helper to create a Firestore Timestamp from Date
export function toTimestamp(date: Date): Timestamp {
  return Timestamp.fromDate(date);
}

// Helper to convert Firestore Timestamp to Date
export function fromTimestamp(timestamp: Timestamp | undefined | null): Date | null {
  if (!timestamp) return null;
  return timestamp.toDate();
}
